/**
 * RevenueCat Web Billing (@revenuecat/purchases-js) — replaces Chargebee.
 *
 * The App User ID MUST be the Firebase uid: the mobile apps identify with
 * `Purchases.logIn(user.uid)`, so using the same id here means a subscription
 * bought on the web is the same RevenueCat customer as on iOS/Android.
 *
 * Entitlement is NOT read from here for access gating — the backend remains the
 * source of truth via `user.subscribed` on GET /me (RevenueCat webhook → API).
 * `isEntitled` is only used for optimistic UI right after a purchase.
 */
import { Purchases } from '@revenuecat/purchases-js';

const WEB_API_KEY = process.env.REACT_APP_REVENUECAT_WEB_KEY;
/** Optional named offering; falls back to the dashboard's "current" offering. */
const OFFERING_ID = process.env.REACT_APP_REVENUECAT_WEB_OFFERING;
export const PREMIUM_ENTITLEMENT = process.env.REACT_APP_REVENUECAT_ENTITLEMENT || 'active';

export function isRevenueCatConfigured() {
  return !!WEB_API_KEY;
}

/** Idempotent — safe to call on every mount of the paywall. */
export function configureRevenueCat(appUserId) {
  if (!WEB_API_KEY) {
    console.warn('[RevenueCat] REACT_APP_REVENUECAT_WEB_KEY is not set — purchases disabled.');
    return null;
  }
  if (!appUserId) {
    console.warn('[RevenueCat] No app user id (Firebase uid) — cannot configure.');
    return null;
  }
  try {
    if (Purchases.isConfigured()) return Purchases.getSharedInstance();
    return Purchases.configure({ apiKey: WEB_API_KEY, appUserId });
  } catch (e) {
    console.warn('[RevenueCat] configure failed:', e);
    return null;
  }
}

/** The offering to sell from: the named one if set, else `current`. */
export async function getWebOffering() {
  if (!Purchases.isConfigured()) return null;
  const offerings = await Purchases.getSharedInstance().getOfferings();
  if (OFFERING_ID && offerings?.all?.[OFFERING_ID]) return offerings.all[OFFERING_ID];
  return offerings?.current ?? null;
}

/**
 * Opens RevenueCat's hosted (Stripe) checkout for a package. Resolves with
 * `{ customerInfo }` on success; rejects if the user cancels or it errors.
 */
export async function purchasePackage(rcPackage, customerEmail) {
  const purchases = Purchases.getSharedInstance();
  return purchases.purchase(customerEmail ? { rcPackage, customerEmail } : { rcPackage });
}

export async function getCustomerInfo() {
  if (!Purchases.isConfigured()) return null;
  return Purchases.getSharedInstance().getCustomerInfo();
}

export function isEntitled(customerInfo) {
  return !!customerInfo?.entitlements?.active?.[PREMIUM_ENTITLEMENT];
}

/** Numeric price (major units) for math like "yearly / 12". */
export function packagePriceNumber(pkg) {
  const price = pkg?.webBillingProduct?.currentPrice ?? pkg?.rcBillingProduct?.currentPrice;
  if (!price) return null;
  if (typeof price.amountMicros === 'number') return price.amountMicros / 1_000_000;
  if (typeof price.amount === 'number') return price.amount;
  return null;
}

/** Pick the monthly / annual packages out of an offering. */
export function splitPackages(offering) {
  if (!offering) return { monthly: null, yearly: null };
  const packages = offering.availablePackages ?? [];
  const monthly =
    offering.monthly ?? packages.find((p) => String(p.packageType).toUpperCase() === 'MONTHLY') ?? null;
  const yearly =
    offering.annual ?? packages.find((p) => String(p.packageType).toUpperCase() === 'ANNUAL') ?? null;
  return { monthly, yearly };
}
